import { lazy, Suspense, useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, Moon, Sun, PottedPlant, FirstAid, SignOut, UserCircle, UsersThree, SlidersHorizontal, PencilSimple, Megaphone, Images } from "@phosphor-icons/react";
import { visibleNav, isExternal } from "../lib/nav";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { UserAvatar, VerifiedBadge } from "./UserAvatar";
import ProfileDialog from "./ProfileDialog";
import NotificationBell from "./NotificationBell";
import CriticalEventDialog from "./CriticalEventDialog";
const AddPlantDialog = lazy(() => import("./AddPlantDialog"));
import { useTheme } from "../lib/theme";
import { useAuth } from "../context/AuthContext";
import { useUpdatesDot } from "../lib/updates";
import { usePendingCount } from "./PendingApprovals";
import { listOpenCritical, authApi } from "../lib/api";
import { acuteBurst } from "../lib/burst";
import { useSite } from "../lib/site";

const pill = { type: "spring", stiffness: 420, damping: 36 };
const item = "rounded-xl py-2";

// The collection page owns its own dialog; everywhere else the header opens one in place.
export const requestAddPlant = () => window.dispatchEvent(new Event(window.location.pathname === "/" ? "gh:add-plant" : "gh:add-plant-here"));

export default function NavBar({ onAddPlant, onCriticalCreated }) {
  const site = useSite();
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const { user, logout, setUser } = useAuth();
  const flipTheme = () => {
    const v = theme === "dark" ? "light" : "dark";
    toggle();
    if (user && user.role !== "kid") authApi.updateMe({ prefs: { ...(user.prefs || {}), theme: v } }).then(setUser).catch(() => {});
  };
  const pendingCount = usePendingCount(user?.role === "admin");
  const updatesDot = useUpdatesDot(!!user && user.role !== "kid");
  const [acuteCount, setAcuteCount] = useState(0);
  useEffect(() => {
    const load = () => listOpenCritical().then((xs) => setAcuteCount(xs.length)).catch(() => {});
    load();
    window.addEventListener("acute:changed", load);
    return () => window.removeEventListener("acute:changed", load);
  }, []);
  const [criticalOpen, setCriticalOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  useEffect(() => {
    const open = () => setAddOpen(true);
    window.addEventListener("gh:add-plant-here", open);
    return () => window.removeEventListener("gh:add-plant-here", open);
  }, []);
  const [profileOpen, setProfileOpen] = useState(false);
  const links = visibleNav(site.nav_items, user);
  const hb = { acute: true, add_plant: true, theme: true, notifications: true, ...(site.header_buttons || {}) };
  const slug = (l) => (l.custom ? `custom-${l.key}` : l.label === l.defaultLabel ? l.label.toLowerCase().replace(/\s+/g, "-") : l.key);

  return (
    <header className="glass sticky top-0 z-40 pt-[env(safe-area-inset-top)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-home-link" className="flex items-center gap-2.5 shrink-0 min-w-0">
          <span className="h-8 w-8 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Leaf weight="duotone" className="h-5 w-5 text-primary" />
          </span>
          <span className="leading-tight hidden sm:block min-w-0">
            <span className="block text-sm font-semibold tracking-tight truncate" data-testid="brand-name">{site.site_name}</span>
            <span className="block text-[11px] text-muted-foreground truncate">{site.tagline}</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5" data-testid="nav-links">
          {links.map((l) => {
            const { key, to, url, label, Icon, end } = l;
            const cls = "relative h-9 px-3 rounded-full text-sm flex items-center gap-1.5 whitespace-nowrap transition-colors";
            if (l.custom && isExternal(url)) return (
              <a key={key} href={url} target="_blank" rel="noreferrer" title={label} data-testid={`nav-${slug(l)}`} className={`${cls} text-muted-foreground hover:text-foreground`}>
                <Icon className="h-4 w-4" /><span className="hidden xl:inline">{label}</span>
              </a>
            );
            return (
            <NavLink key={key} to={to || url} end={end} title={label} data-testid={`nav-${slug(l)}`}
              className={({ isActive }) => `${cls} ${isActive ? "text-foreground font-medium" : "text-muted-foreground hover:text-foreground"}`}>
              {({ isActive }) => (
                <>
                  {isActive && <motion.span layoutId="nav-pill" className="absolute inset-0 rounded-full bg-muted" transition={pill} />}
                  <Icon weight={isActive ? "fill" : "regular"} className="relative h-4 w-4" />
                  <span className="relative hidden xl:inline">{label}</span>
                </>
              )}
            </NavLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-1.5">
          {hb.acute && <button type="button" data-testid="header-critical-btn" data-active={acuteCount > 0} aria-label="Log acute event" title="Log acute event"
            onClick={(e) => { acuteBurst(e); setTimeout(() => setCriticalOpen(true), 160); }}
            className={`relative h-9 w-9 sm:w-auto sm:pl-3 sm:pr-3.5 rounded-full inline-flex shrink-0 whitespace-nowrap items-center justify-center gap-1.5 text-[13px] font-semibold text-[#FF3B30] transition-[background-color,transform] duration-200 active:scale-[0.96] ${
              acuteCount > 0 ? "bg-[#FF3B30]/[0.14] hover:bg-[#FF3B30]/[0.2]" : "bg-[#FF3B30]/[0.07] hover:bg-[#FF3B30]/[0.12]"}`}>
            <FirstAid weight="bold" className="h-4 w-4" />
            <span className="hidden sm:inline">Acute event</span>
            {acuteCount > 0 && <span data-testid="header-acute-count" className="hidden sm:inline-flex h-5 min-w-5 px-1.5 rounded-full bg-[#FF3B30] text-white text-[10px] font-semibold items-center justify-center tabular-nums">{acuteCount}</span>}
            {acuteCount > 0 && <span className="sm:hidden absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-[#FF3B30] ring-2 ring-background" />}
          </button>}
          {user && hb.add_plant && (
            <button type="button" data-testid="header-add-plant-btn" onClick={() => (onAddPlant ? onAddPlant() : requestAddPlant())}
              className="h-9 w-9 sm:w-auto sm:pl-3 sm:pr-3.5 rounded-full inline-flex items-center justify-center gap-1.5 shrink-0 whitespace-nowrap text-[13px] font-medium text-[#30A14E] bg-[#30A14E]/[0.06] ring-1 ring-inset ring-[#30A14E]/40 hover:bg-[#30A14E]/[0.14] hover:ring-[#30A14E]/60 hover:-translate-y-px active:translate-y-0 active:scale-[0.96] transition-[background-color,transform,box-shadow] duration-200">
              <PottedPlant weight="fill" className="h-4 w-4" />
              <span className="hidden sm:inline">New plant</span>
            </button>
          )}

          {(hb.acute || hb.add_plant) && <span aria-hidden className="hidden sm:block h-5 w-px bg-border mx-1.5" />}

          {hb.theme && <Button data-testid="theme-toggle-btn" variant="ghost" size="icon" onClick={flipTheme} className="rounded-full h-9 w-9 text-muted-foreground hover:text-foreground" aria-label="Toggle theme" title={theme === "dark" ? "Light mode" : "Dark mode"}>
            <motion.span key={theme} initial={{ rotate: -40, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} transition={pill} className="inline-flex">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </motion.span>
          </Button>}
          {user && hb.notifications && <NotificationBell />}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 h-9 pl-1 pr-1 md:pr-3 lg:pr-1 2xl:pr-3 rounded-full hover:bg-muted transition-colors">
                  <span className="relative">
                    <UserAvatar user={user} className="h-7 w-7 text-[11px]" testid="nav-user-avatar" />
                    {(pendingCount > 0 || updatesDot) && <span data-testid="nav-pending-dot" className={`absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-background ${pendingCount > 0 ? "bg-[#FF9F0A]" : "bg-[#FF375F]"}`} />}
                  </span>
                  <span className="text-sm font-medium hidden md:inline lg:hidden 2xl:inline max-w-[120px] truncate" data-testid="nav-user-name">{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={10} className="rounded-2xl w-64 p-1.5">
                <DropdownMenuLabel className="font-normal flex items-center gap-3 py-2">
                  <UserAvatar user={user} className="h-9 w-9 text-xs" />
                  <span className="min-w-0">
                    <span className="text-sm font-medium flex items-center gap-1">{user.name} <VerifiedBadge user={user} className="h-3.5 w-3.5" />{user.role === "admin" && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">Admin</span>}</span>
                    <span className="block text-xs text-muted-foreground truncate">{user.email}</span>
                  </span>
                </DropdownMenuLabel>
                <div className="lg:hidden">
                  <DropdownMenuSeparator />
                  {links.map(({ key, to, url, label, Icon, custom }) => (
                    <DropdownMenuItem key={key} asChild className={item}>{custom && isExternal(url)
                      ? <a href={url} target="_blank" rel="noreferrer" data-testid={`menu-nav-${key}`}><Icon className="h-4 w-4 mr-2.5" /> {label}</a>
                      : <Link to={to || url} data-testid={`menu-nav-${key}`}><Icon className="h-4 w-4 mr-2.5" /> {label}</Link>}</DropdownMenuItem>
                  ))}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild className={item} data-testid="menu-my-profile"><Link to="/me"><UserCircle className="h-4 w-4 mr-2.5" /> My profile</Link></DropdownMenuItem>
                <DropdownMenuItem className={item} data-testid="menu-profile" onClick={() => setProfileOpen(true)}><PencilSimple className="h-4 w-4 mr-2.5" /> Edit profile</DropdownMenuItem>
                <DropdownMenuItem asChild className={item} data-testid="menu-updates"><Link to="/updates"><Megaphone className="h-4 w-4 mr-2.5" /> What's new
                  {updatesDot && <span data-testid="menu-updates-dot" className="ml-auto h-2 w-2 rounded-full bg-[#FF375F]" />}
                </Link></DropdownMenuItem>
                {user.role === "admin" && (
                <DropdownMenuItem asChild className={item} data-testid="menu-team">
                  <Link to="/team"><UsersThree className="h-4 w-4 mr-2.5" /> Manage people
                    {pendingCount > 0 && <span data-testid="nav-pending-badge" className="ml-auto text-[10px] font-semibold min-w-5 h-5 px-1.5 rounded-full bg-[#FF9F0A] text-white flex items-center justify-center">{pendingCount}</span>}
                  </Link>
                </DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem asChild className={item} data-testid="menu-settings"><Link to="/settings"><SlidersHorizontal className="h-4 w-4 mr-2.5" /> Site options</Link></DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem asChild className={item} data-testid="menu-photos"><Link to="/photos"><Images className="h-4 w-4 mr-2.5" /> Photo library</Link></DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem className={item} data-testid="menu-signout" onClick={logout}><SignOut className="h-4 w-4 mr-2.5" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      <CriticalEventDialog open={criticalOpen} onOpenChange={setCriticalOpen} onCreated={onCriticalCreated} />
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
      {addOpen && <Suspense fallback={null}><AddPlantDialog open={addOpen} onOpenChange={setAddOpen} /></Suspense>}
    </header>
  );
}
